import React, { MouseEvent } from 'react';
import cx from 'classnames';
import ChevronS1Right from '../Icons/ChevronS1Right';
import ChevronS2Right from '../Icons/ChevronS2Right';
import Checkmark from '../Icons/Checkmark';
import LoadingSpinnerNoBits from '../LoadingSpinnerNoBits';
import Touchable, { TouchableProps } from '../Touchable';

import styles from './Button.module.scss';

export interface Props extends Omit<TouchableProps, 'size'> {
  animatedCaret?: boolean;
  flexContainerClassName?: string;
  icon?: React.ReactNode;
  onClick?: (e: MouseEvent) => void;
  secondary?: boolean;
  checked?: boolean;
  size?: 32 | 40 | 48 | 56;
  showLoader?: boolean;
  submitting?: boolean;
  tertiary?: boolean;
  textClassName?: string;
  // deprecated props
  large?: boolean;
  small?: boolean;
}

export const Button: React.ForwardRefExoticComponent<Props> = React.forwardRef(
  (
    {
      animatedCaret,
      centered,
      checked,
      children,
      className,
      disabled,
      flexContainerClassName,
      icon,
      inline,
      large,
      role,
      secondary,
      showLoader,
      size,
      small,
      submitting,
      tertiary,
      textClassName,
      // important! props that are destructured here that Touchable
      // still relies on must be passed to Touchable
      ...restProps
    },
    ref: any,
  ) => {
    const showIcon = centered && icon;
    const showCheckedIcon = !tertiary && !secondary && centered && checked;
    return (
      <Touchable
        block={!inline}
        className={cx(
          styles.button,
          disabled && styles.disabled,
          inline && styles.inline,
          (size === 56 || large) && styles.size56,
          size === 40 && styles.size40,
          (size === 32 || small) && styles.size32,
          submitting && styles.submitting,
          secondary && styles.secondary,
          tertiary && styles.tertiary,
          (centered || tertiary) && styles.centered,
          className,
        )}
        disabled={disabled || submitting}
        role={role ? role : 'button'}
        inline={inline}
        centered={centered || tertiary}
        ref={ref}
        {...restProps}
      >
        <span className={cx(styles.flex, flexContainerClassName)}>
          {(showIcon || showCheckedIcon) && (
            <span className={styles.leadingIcon}>
              {showIcon ? icon : showCheckedIcon && <Checkmark />}
            </span>
          )}
          {showLoader && submitting && (
            <LoadingSpinnerNoBits
              className={styles.spinnerContainer}
              spinnerClassName={styles.spinner}
              variant='single-color'
            />
          )}
          <span className={cx(styles.text, textClassName)}>{children}</span>
          {!centered && !tertiary && (
            <span
              className={cx(
                styles.caret,
                animatedCaret && styles.animatedCaret,
              )}
            >
              {size === 32 || small ? (
                <ChevronS2Right
                  className={cx(
                    styles.icon,
                    animatedCaret && styles.secondaryIcon,
                  )}
                />
              ) : (
                <ChevronS1Right
                  className={cx(
                    styles.icon,
                    animatedCaret && styles.secondaryIcon,
                  )}
                />
              )}
              {animatedCaret &&
                (size === 32 || small ? (
                  <ChevronS2Right className={styles.icon} />
                ) : (
                  <ChevronS1Right className={styles.icon} />
                ))}
            </span>
          )}
        </span>
      </Touchable>
    );
  },
);

// Button.defaultProps = { appearance: Appearance.PRIMARY };
Button.displayName = 'Button';
export default Button;
export { styles };
